import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, GripHorizontal } from "lucide-react"
import { cn } from "@/lib/utils"
import { resolveOpener } from "@/lib/a11y/menuOpener"

// WCAG 2.4.3: Radix only restores focus to a <DialogTrigger>; a controlled dialog (open prop, no trigger) dropped focus to <body>.
// The Root remembers the element focused when `open` turned true; the Content hands focus back to it on close.
const OpenerContext = React.createContext<React.MutableRefObject<HTMLElement | null> | null>(null)

const Dialog = (props: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const openerRef = React.useRef<HTMLElement | null>(null)
  const wasOpenRef = React.useRef(false)
  const isOpen = !!props.open
  if (isOpen !== wasOpenRef.current) {
    wasOpenRef.current = isOpen
    // Read while rendering: nothing inside the dialog has taken focus yet.
    // A focused menu item is about to unmount with its menu: remember the menu's trigger instead.
    if (isOpen && typeof document !== 'undefined') openerRef.current = resolveOpener(document.activeElement)
  }
  return (
    <OpenerContext.Provider value={openerRef}>
      <DialogPrimitive.Root {...props} />
    </OpenerContext.Provider>
  )
}

function useRestoreOpenerFocus(onCloseAutoFocus?: (event: Event) => void) {
  const openerRef = React.useContext(OpenerContext)
  return React.useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event)
      if (event.defaultPrevented) return
      const opener = openerRef?.current
      if (opener && opener !== document.body && opener.isConnected) {
        event.preventDefault()
        opener.focus()
      }
    },
    [openerRef, onCloseAutoFocus],
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

function useResetBodyPointerEvents() {
  React.useEffect(() => {
    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, []);
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // Safety net: reset body pointer-events on unmount (Radix can leave stale state)
  useResetBodyPointerEvents();

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[10000] bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  // Safety net: reset body pointer-events on unmount
  useResetBodyPointerEvents();
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);

  return (
  <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onCloseAutoFocus={restoreFocus}
      className={cn(
        "fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-edge bg-panel p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

// Nested dialog content with higher z-index for dialogs within dialogs
// NOTE: Parent dialogs may use inline styles up to z-index: 99999, so nested must be higher
const DialogContentNested = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  return (
  <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
    <DialogPrimitive.Overlay
      className="fixed inset-0 z-[100100] bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogPrimitive.Content
      ref={ref}
      onCloseAutoFocus={restoreFocus}
      className={cn(
        "fixed left-[50%] top-[50%] z-[100101] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-edge bg-panel p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-sm text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContentNested.displayName = "DialogContentNested"

// Draggable, non-modal dialog content (no overlay/darkening)
const DialogContentDraggable = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onCloseAutoFocus, ...props }, ref) => {
  const restoreFocus = useRestoreOpenerFocus(onCloseAutoFocus);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
      {/* No overlay - no page darkening */}
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={undefined}
        onCloseAutoFocus={restoreFocus}
        className={cn(
          "fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg gap-4 border border-edge bg-panel p-6 pt-0 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg text-ink max-h-[100dvh] overflow-y-auto [animation-fill-mode:backwards]",
          className
        )}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        }}
        {...props}
      >
        {/* Drag handle header with close button */}
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            "flex items-center h-8 cursor-move rounded-t-lg bg-floating/50 hover:bg-floating/70 transition-colors -mx-6 px-2",
            isDragging && "bg-floating"
          )}
        >
          <GripHorizontal className="w-4 h-4 text-ink-muted" />
          <div className="flex-1" />
          <DialogPrimitive.Close
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none p-1 hover:bg-floating/50"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>
        <div>
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContentDraggable.displayName = "DialogContentDraggable"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogContentNested,
  DialogContentDraggable,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
